import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  type DocumentData,
  type Query,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../../lib/firestore';
import { devError } from '../../utils/dev-log';
import type { HandoverRequest } from '../../types';

const COLLECTION_NAME = 'handover_requests';

function normalizeStatus(status: unknown): HandoverRequest['status'] | undefined {
  if (status === 'waiting') return 'pending';
  if (status === 'in_progress') return 'active';
  if (status === 'pending' || status === 'assigned' || status === 'active' || status === 'resolved' || status === 'cancelled') {
    return status;
  }
  return undefined;
}

function toDateValue(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate().getTime();
  }
  return 0;
}

function normalizeHandover(raw: DocumentData, id: string): HandoverRequest {
  const status = normalizeStatus(raw.status) ?? 'pending';
  return {
    id,
    sessionId: String(raw.sessionId ?? ''),
    propertyId: String(raw.propertyId ?? ''),
    guestId: String(raw.guestId ?? ''),
    requestedAt: raw.requestedAt ?? raw.createdAt ?? new Date(0),
    reason: String(raw.reason ?? ''),
    priority: raw.priority === 'low' || raw.priority === 'medium' || raw.priority === 'high' || raw.priority === 'urgent'
      ? raw.priority
      : 'medium',
    category: raw.category === 'booking' || raw.category === 'complaint' || raw.category === 'technical' || raw.category === 'general'
      ? raw.category
      : undefined,
    guestName: typeof raw.guestName === 'string' ? raw.guestName : undefined,
    guestEmail: typeof raw.guestEmail === 'string' ? raw.guestEmail : undefined,
    status,
    assignedAgent: typeof raw.assignedAgent === 'string'
      ? raw.assignedAgent
      : typeof raw.assignedAgentId === 'string'
        ? raw.assignedAgentId
        : undefined,
    assignedAt: raw.assignedAt ?? undefined,
    conversationHistory: Array.isArray(raw.conversationHistory) ? (raw.conversationHistory as HandoverRequest['conversationHistory']) : [],
    aiAnalysis: raw.aiAnalysis as Record<string, unknown> | undefined,
    guestProfile: raw.guestProfile as Record<string, unknown> | undefined,
  };
}

function mergeHandovers(primary: HandoverRequest[], legacy: HandoverRequest[]): HandoverRequest[] {
  const merged = new Map<string, HandoverRequest>();
  for (const handover of legacy) merged.set(handover.id, handover);
  for (const handover of primary) merged.set(handover.id, handover);

  return [...merged.values()].sort((a, b) => {
    const aTime = toDateValue(a.requestedAt ?? a.assignedAt);
    const bTime = toDateValue(b.requestedAt ?? b.assignedAt);
    if (aTime !== bTime) return bTime - aTime;
    return a.id.localeCompare(b.id);
  });
}

function buildStatusConstraint(status?: HandoverRequest['status']): QueryConstraint[] {
  if (!status) return [];
  if (status === 'pending') {
    return [where('status', 'in', ['pending', 'waiting'])];
  }
  if (status === 'active') {
    return [where('status', 'in', ['active', 'in_progress'])];
  }
  return [where('status', '==', status)];
}

function buildPropertyQueueConstraints(propertyId: string, status?: HandoverRequest['status']): QueryConstraint[] {
  return [
    where('propertyId', '==', propertyId),
    ...buildStatusConstraint(status),
    orderBy('createdAt', 'desc'),
  ];
}

async function fetchQueryResults(q: Query<DocumentData>): Promise<HandoverRequest[]> {
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => normalizeHandover(docSnap.data(), docSnap.id));
}

export class HandoversService {
  private getPropertyCollection(propertyId: string) {
    return collection(db, 'properties', propertyId, COLLECTION_NAME);
  }

  private getLegacyCollection() {
    return collection(db, COLLECTION_NAME);
  }

  private subscribeToMergedCollections(
    primaryQuery: Query<DocumentData>,
    legacyQuery: Query<DocumentData>,
    onUpdate: (handovers: HandoverRequest[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    let primary: HandoverRequest[] = [];
    let legacy: HandoverRequest[] = [];
    let primaryError: Error | null = null;
    let legacyError: Error | null = null;

    const emit = () => {
      onUpdate(mergeHandovers(primary, legacy));
    };

    const handleError = (source: 'primary' | 'legacy', error: Error) => {
      devError(`Error subscribing to ${source} handovers:`, error);
      if (source === 'primary') primaryError = error;
      else legacyError = error;
      if (primaryError && legacyError) {
        onError?.(primaryError ?? legacyError);
      }
    };

    const unsubscribePrimary = onSnapshot(
      primaryQuery,
      (snapshot) => {
        primary = snapshot.docs.map((docSnap) => normalizeHandover(docSnap.data(), docSnap.id));
        emit();
      },
      (error) => handleError('primary', error),
    );

    const unsubscribeLegacy = onSnapshot(
      legacyQuery,
      (snapshot) => {
        legacy = snapshot.docs.map((docSnap) => normalizeHandover(docSnap.data(), docSnap.id));
        emit();
      },
      (error) => handleError('legacy', error),
    );

    return () => {
      unsubscribePrimary();
      unsubscribeLegacy();
    };
  }

  async getQueue(propertyId: string, status?: HandoverRequest['status']): Promise<HandoverRequest[]> {
    const primaryQuery = query(this.getPropertyCollection(propertyId), ...buildStatusConstraint(status), orderBy('createdAt', 'desc'));
    const legacyQuery = query(this.getLegacyCollection(), ...buildPropertyQueueConstraints(propertyId, status));

    const [primary, legacy] = await Promise.all([
      fetchQueryResults(primaryQuery),
      fetchQueryResults(legacyQuery),
    ]);

    return mergeHandovers(primary, legacy);
  }

  subscribeToQueue(
    propertyId: string,
    onUpdate: (handovers: HandoverRequest[]) => void,
    onError?: (error: Error) => void,
    status?: HandoverRequest['status'],
  ): Unsubscribe {
    const primaryQuery = query(
      this.getPropertyCollection(propertyId),
      ...buildStatusConstraint(status),
      orderBy('createdAt', 'desc'),
    );
    const legacyQuery = query(
      this.getLegacyCollection(),
      ...buildPropertyQueueConstraints(propertyId, status),
    );

    return this.subscribeToMergedCollections(primaryQuery, legacyQuery, onUpdate, onError);
  }

  subscribeToPendingHandovers(
    propertyId: string,
    onUpdate: (handovers: HandoverRequest[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    return this.subscribeToQueue(propertyId, onUpdate, onError, 'pending');
  }

  subscribeToAgentHandovers(
    agentId: string,
    onUpdate: (handovers: HandoverRequest[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const statuses: Array<HandoverRequest['status'] | 'in_progress'> = ['assigned', 'active', 'in_progress'];
    const primaryQuery = query(
      collectionGroup(db, COLLECTION_NAME),
      where('assignedAgent', '==', agentId),
      where('status', 'in', statuses),
      orderBy('createdAt', 'desc'),
    );
    const legacyQuery = query(
      this.getLegacyCollection(),
      where('assignedAgent', '==', agentId),
      where('status', 'in', statuses),
      orderBy('createdAt', 'desc'),
    );

    return this.subscribeToMergedCollections(primaryQuery, legacyQuery, onUpdate, onError);
  }

  async updateHandover(propertyId: string, handoverId: string, updates: Partial<HandoverRequest>): Promise<void> {
    const payload = {
      ...updates,
      propertyId,
      id: handoverId,
    };

    // Writes go only to the property-scoped path as of the 2026-04-11 migration
    // cutover. Reads still merge the legacy root collection while backfill runs
    // (see subscribeToQueue). Once the cleanup step of
    // docs/runbooks/handover-requests-migration.md completes the legacy reads
    // can be removed.
    await setDoc(doc(db, 'properties', propertyId, COLLECTION_NAME, handoverId), payload, { merge: true });
  }
}

export const handoversService = new HandoversService();
